import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FLASH_MESSAGE_EVENT } from '../HelperFunctions';
import './ToastViewport.css';

const TOAST_TITLES = {
  success: 'Done',
  error: 'Something went wrong',
  warning: 'Needs attention',
  info: 'Just so you know',
};

const EXIT_DURATION = 180;
const MAX_VISIBLE_TOASTS = 4;

function ToastIcon({ type }) {
  if (type === 'success') return <span aria-hidden="true">✓</span>;
  if (type === 'error') return <span aria-hidden="true">!</span>;
  if (type === 'warning') return <span aria-hidden="true">!</span>;
  return <span aria-hidden="true">i</span>;
}

export default function ToastViewport() {
  const [toasts, setToasts] = useState([]);
  const dismissTimers = useRef(new Map());
  const exitTimers = useRef(new Map());

  const removeToast = useCallback((id) => {
    window.clearTimeout(dismissTimers.current.get(id));
    dismissTimers.current.delete(id);
    setToasts(current => current.map(toast => (
      toast.id === id ? { ...toast, exiting: true } : toast
    )));
    const exitTimer = window.setTimeout(() => {
      setToasts(current => current.filter(toast => toast.id !== id));
      exitTimers.current.delete(id);
    }, EXIT_DURATION);
    exitTimers.current.set(id, exitTimer);
  }, []);

  useEffect(() => {
    function handleFlashMessage(event) {
      const toast = event.detail;
      if (!toast?.id || !toast?.message) return;
      setToasts(current => [...current, toast].slice(-MAX_VISIBLE_TOASTS));
      const timer = window.setTimeout(() => removeToast(toast.id), toast.duration);
      dismissTimers.current.set(toast.id, timer);
    }

    window.addEventListener(FLASH_MESSAGE_EVENT, handleFlashMessage);
    const activeDismissTimers = dismissTimers.current;
    const activeExitTimers = exitTimers.current;
    return () => {
      window.removeEventListener(FLASH_MESSAGE_EVENT, handleFlashMessage);
      activeDismissTimers.forEach(timer => window.clearTimeout(timer));
      activeExitTimers.forEach(timer => window.clearTimeout(timer));
    };
  }, [removeToast]);

  if (!toasts.length) return null;

  return (
    <div className="say-toast-viewport" aria-label="Notifications">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`say-toast say-toast-${toast.type}${toast.exiting ? ' is-exiting' : ''}`}
          role={toast.type === 'error' ? 'alert' : 'status'}
          aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <div className="say-toast-icon"><ToastIcon type={toast.type} /></div>
          <div className="say-toast-copy">
            <strong>{TOAST_TITLES[toast.type]}</strong>
            <p>{toast.message}</p>
          </div>
          <button type="button" className="say-toast-close" aria-label="Dismiss notification" onClick={() => removeToast(toast.id)}>×</button>
          <span className="say-toast-timer" aria-hidden="true" style={{ animationDuration: `${toast.duration}ms` }} />
        </div>
      ))}
    </div>
  );
}
